import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  Music2, Mail, Sparkles, Globe2, Facebook, Linkedin, Star,
  Bot, Send, Loader2, FileText, Download, ArrowRight, MessageCircle, Eye,
} from "lucide-react";
import ActivityFeed from "@/components/client_portal/ActivityFeed";

const CHANNEL_ICON = {
  music: Music2,
  mail: Mail,
  sparkles: Sparkles,
  globe: Globe2,
  facebook: Facebook,
  linkedin: Linkedin,
  star: Star,
};

const fmt = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return (v / 1_000_000).toFixed(1) + "M";
  if (v >= 1_000) return (v / 1_000).toFixed(1) + "K";
  return v.toLocaleString();
};

export default function ClientDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [params] = useSearchParams();
  const { user } = useAuth();
  const adminPreviewClientId = params.get("client_id");
  const isAdminPreview = user?.role === "admin" && !!adminPreviewClientId;
  const queryString = isAdminPreview ? `?client_id=${encodeURIComponent(adminPreviewClientId)}` : "";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/client-portal/summary${queryString}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) toast.error(e.response?.data?.detail || "Failed to load dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [queryString]);

  const brandPrimary = data?.client?.brand_colors?.primary || "#002FA7";

  if (loading) {
    return (
      <div className="p-12 text-center text-sm text-zinc-500" data-testid="client-dashboard-loading">
        <Loader2 className="h-5 w-5 animate-spin inline mr-2" /> Loading your dashboard…
      </div>
    );
  }

  if (!data) {
    return <div className="p-8 text-sm text-zinc-500">No data.</div>;
  }

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 lg:p-8" data-testid="client-dashboard-page">
      {isAdminPreview && (
        <div className="rounded-sm border border-amber-300 bg-amber-50 px-4 py-2.5 flex items-center justify-between gap-3 flex-wrap"
          data-testid="admin-preview-banner">
          <div className="flex items-center gap-2 text-[12px] text-amber-900">
            <Eye className="h-3.5 w-3.5" />
            <span>You&apos;re viewing this dashboard <strong>as the client</strong> would see it — admin preview mode (no data is hidden, no actions write back to the client account).</span>
          </div>
          <Link to={`/clients/${adminPreviewClientId}/contacts`}
            className="text-[11px] font-mono text-amber-900 hover:underline"
            data-testid="admin-preview-back">
            ← back to admin
          </Link>
        </div>
      )}
      {/* Hero */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          {data.client.logo_url && (
            <img src={`${process.env.REACT_APP_BACKEND_URL}${data.client.logo_url}`}
              alt={data.client.company_name}
              className="h-14 w-auto object-contain rounded-sm" />
          )}
          <div>
            <div className="label-overline" style={{ color: brandPrimary }}>
              Marketing dashboard
            </div>
            <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="client-company">
              {data.client.company_name}
            </h1>
          </div>
        </div>
      </div>

      {/* Top-level KPI tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dashboard-kpis">
        <KpiTile label="Channels connected" value={data.kpis.channels_connected}
          accent={brandPrimary} testId="kpi-channels" />
        <KpiTile label="Total followers" value={fmt(data.kpis.followers_total)}
          accent={brandPrimary} testId="kpi-followers" />
        <KpiTile label="Reviews collected" value={fmt(data.kpis.reviews_total)}
          accent={brandPrimary} testId="kpi-reviews" />
        <KpiTile label="Reports published" value={data.kpis.reports_published}
          accent={brandPrimary} testId="kpi-reports" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Channel cards */}
        <div className="lg:col-span-2 space-y-4">
          <SectionHeader title="Channels" subtitle="A quick look at every connected platform" />
          {data.channels.length === 0 ? (
            <Card className="rounded-sm border-dashed border-zinc-300" data-testid="no-channels-card">
              <CardContent className="p-6 text-sm text-zinc-500 text-center">
                No channels have been connected yet. Your account manager will let you know once data starts flowing.
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="channels-grid">
              {data.channels.map((c) => (
                <ChannelCard key={c.key} channel={c} accent={brandPrimary}
                  adminClientId={isAdminPreview ? adminPreviewClientId : null} />
              ))}
            </div>
          )}

          {/* Recent reports */}
          <SectionHeader title="Recent reports" subtitle="Your monthly marketing reviews" />
          {data.recent_reports.length === 0 ? (
            <Card className="rounded-sm border-dashed border-zinc-300">
              <CardContent className="p-6 text-sm text-zinc-500 text-center">
                No published reports yet.
              </CardContent>
            </Card>
          ) : (
            <Card className="rounded-sm border-zinc-200" data-testid="recent-reports-card">
              <CardContent className="p-0 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50/60 border-b border-zinc-100">
                    <tr className="text-left">
                      <th className="px-4 py-2 label-overline">Period</th>
                      <th className="px-4 py-2 label-overline">Status</th>
                      <th className="px-4 py-2 label-overline">Generated</th>
                      <th className="px-4 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recent_reports.map((r) => (
                      <tr key={r.report_id} className="border-b border-zinc-50"
                          data-testid={`report-row-${r.report_id}`}>
                        <td className="px-4 py-2 font-mono text-[12px]">{r.period_month}</td>
                        <td className="px-4 py-2">
                          <Badge variant="outline" className="rounded-sm text-[10px] uppercase">
                            {r.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-2 text-[11px] font-mono text-zinc-500">
                          {r.generated_at ? new Date(r.generated_at).toLocaleDateString() : "—"}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <Link to={`/portal/reports/${r.report_id}/view`}
                            className="text-[12px] inline-flex items-center gap-1 hover:underline"
                            style={{ color: brandPrimary }}
                            data-testid={`open-report-${r.report_id}`}>
                            <FileText className="h-3 w-3" /> Open
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Ask AI rail */}
        <div className="space-y-4">
          <ActivityFeed accent={brandPrimary} />
          <AskAIPanel accent={brandPrimary}
            adminPreviewClientId={isAdminPreview ? adminPreviewClientId : null} />
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ title, subtitle }) {
  return (
    <div className="flex items-baseline gap-3 mt-2">
      <h2 className="font-display font-bold text-base tracking-tight">{title}</h2>
      <span className="text-[11px] text-zinc-500">{subtitle}</span>
    </div>
  );
}

function KpiTile({ label, value, accent, testId }) {
  return (
    <Card className="rounded-sm border-zinc-200" data-testid={testId}>
      <CardContent className="p-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 mb-1.5">{label}</div>
        <div className="font-display font-bold text-2xl tracking-tight" style={{ color: accent }}>
          {value}
        </div>
      </CardContent>
    </Card>
  );
}

function ChannelCard({ channel, accent, adminClientId }) {
  const Icon = CHANNEL_ICON[channel.icon] || Globe2;
  const href = channel.drill_down + (adminClientId
    ? `${channel.drill_down.includes("?") ? "&" : "?"}client_id=${encodeURIComponent(adminClientId)}`
    : "");
  return (
    <Link to={href}
      className="block group"
      data-testid={`channel-card-${channel.key}`}>
      <Card className="rounded-sm border-zinc-200 h-full transition-shadow group-hover:shadow-md">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-sm flex items-center justify-center text-white"
                style={{ background: accent }}>
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <div className="font-semibold text-sm">{channel.label}</div>
                {channel.subtitle && (
                  <div className="text-[10px] font-mono text-zinc-500">{channel.subtitle}</div>
                )}
              </div>
            </div>
            <ArrowRight className="h-3.5 w-3.5 text-zinc-400 group-hover:text-zinc-700" />
          </div>
          {channel.headline?.length > 0 && (
            <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-100">
              {channel.headline.slice(0, 4).map((h, i) => (
                <div key={`${channel.key}-h-${i}`}>
                  <div className="text-[9px] uppercase tracking-[0.14em] text-zinc-500">{h.label}</div>
                  <div className="font-mono text-[14px] font-semibold">{typeof h.value === "number" ? fmt(h.value) : (h.value ?? "—")}</div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

function AskAIPanel({ accent, adminPreviewClientId }) {
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const endRef = useRef();

  const send = useCallback(async () => {
    const q = (draft || "").trim();
    if (!q) return;
    setSending(true);
    const optimistic = [...messages, { role: "user", content: q }];
    setMessages(optimistic);
    setDraft("");
    try {
      const { data } = await api.post("/client-portal/ask", {
        question: q,
        conversation_id: conversationId,
        ...(adminPreviewClientId ? { client_id: adminPreviewClientId } : {}),
      });
      setConversationId(data.conversation_id);
      setMessages(data.history.map((m) => ({ role: m.role, content: m.content })));
    } catch (e) {
      setMessages(optimistic.concat([{
        role: "assistant",
        content: e.response?.data?.detail || "I couldn't reach the AI right now. Please try again in a moment.",
      }]));
    } finally {
      setSending(false);
    }
  }, [draft, messages, conversationId, adminPreviewClientId]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  const suggestions = useMemo(() => [
    "What were our top channels last month?",
    "Where should we invest more next month?",
    "Which content is driving the most engagement?",
  ], []);

  return (
    <Card className="rounded-sm border-zinc-200 lg:sticky lg:top-4 h-fit"
      data-testid="ask-ai-panel">
      <CardContent className="p-0 flex flex-col">
        <div className="px-4 py-3 border-b border-zinc-100 flex items-center gap-2"
          style={{ background: `${accent}11` }}>
          <div className="h-7 w-7 rounded-sm flex items-center justify-center text-white"
            style={{ background: accent }}>
            <Bot className="h-3.5 w-3.5" />
          </div>
          <div>
            <div className="font-display font-bold text-sm tracking-tight">Ask AI</div>
            <div className="text-[10px] font-mono text-zinc-600">Powered by Claude · context from your reports</div>
          </div>
        </div>

        <div className="px-4 py-3 max-h-[420px] min-h-[280px] overflow-y-auto space-y-3"
          data-testid="ask-ai-messages">
          {messages.length === 0 && (
            <>
              <div className="text-[12px] text-zinc-600">
                Ask me anything about your marketing reports — channel performance, trends, what to do next month.
              </div>
              <div className="space-y-1.5">
                {suggestions.map((s) => (
                  <button
                    type="button"
                    key={s}
                    onClick={() => setDraft(s)}
                    className="block w-full text-left text-[11px] px-2.5 py-1.5 rounded-sm border border-zinc-200 text-zinc-600 hover:bg-zinc-50"
                    data-testid={`ask-ai-suggestion-${s.length}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </>
          )}
          {messages.map((m, i) => (
            <div key={`m-${i}`}
              className={m.role === "user" ? "text-right" : ""}
              data-testid={`ask-ai-msg-${i}`}>
              <div
                className={`inline-block max-w-[90%] rounded-sm px-3 py-2 text-[12px] leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "text-white"
                    : "bg-zinc-100 text-zinc-800"
                }`}
                style={m.role === "user" ? { background: accent } : undefined}
              >
                {m.content}
              </div>
            </div>
          ))}
          {sending && (
            <div className="text-[11px] text-zinc-500 flex items-center gap-1.5">
              <Loader2 className="h-3 w-3 animate-spin" /> thinking…
            </div>
          )}
          <div ref={endRef} />
        </div>

        <div className="px-3 py-2.5 border-t border-zinc-100 flex items-center gap-2">
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !sending) send(); }}
            placeholder="Ask a question about your reports…"
            className="rounded-sm text-[13px] h-9"
            data-testid="ask-ai-input"
          />
          <Button
            onClick={send}
            disabled={sending || !draft.trim()}
            className="rounded-sm text-white h-9 px-3"
            style={{ background: accent }}
            data-testid="ask-ai-send"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
